// Axios singleton + interceptors.
//   - Attach Authorization: Bearer <accessToken> from the store.
//   - On 401, attempt one silent refresh via /api/admin/auth/refresh (cookie-based);
//     dedupe concurrent refreshes via a shared pending promise.
//   - On refresh fail, clear auth state so the next render hits the login page.

import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../features/auth/auth.store';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

let pendingRefresh: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (!pendingRefresh) {
    pendingRefresh = axios
      .post<{ accessToken: string; user: { id: string; username: string; role: 'admin' | 'manager' } }>(
        `${API_BASE}/admin/auth/refresh`,
        {},
        { withCredentials: true },
      )
      .then((res) => {
        useAuthStore.getState().setAuth(res.data.user, res.data.accessToken);
        return res.data.accessToken;
      })
      .catch((err) => {
        useAuthStore.getState().clear();
        throw err;
      })
      .finally(() => {
        pendingRefresh = null;
      });
  }
  return pendingRefresh;
}

api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (err.response?.status === 401 && original && !original._retried) {
      // Don't try to refresh on the refresh / login endpoints themselves.
      const url = original.url ?? '';
      if (url.includes('/auth/refresh') || url.includes('/auth/login')) {
        return Promise.reject(err);
      }
      original._retried = true;
      try {
        await refreshAccessToken();
        return api(original);
      } catch {
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  },
);

export async function getData<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.get<T>(url, config);
  return res.data;
}
export async function postData<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.post<T>(url, body, config);
  return res.data;
}
export async function patchData<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.patch<T>(url, body, config);
  return res.data;
}
export async function deleteData<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await api.delete<T>(url, config);
  return res.data;
}
